const UPSELL_SKELETON = `
  <div class="sp-bg-secondaryLighter sp-flex sp-flex-col sp-h-full sp-overflow-hidden sp-rounded-sm">
    <div class="sp-animate-pulse sp-aspect-[231/176] sp-bg-secondaryLight"></div>
    <div class="sp-flex sp-flex-col sp-gap-[12px] sp-p-[12px]">
      <div class="sp-animate-pulse sp-bg-secondaryLight sp-h-[14px] sp-rounded-sm sp-w-3/4"></div>
      <div class="sp-animate-pulse sp-bg-secondaryLight sp-h-[14px] sp-rounded-sm sp-w-1/2"></div>
      <div class="sp-animate-pulse sp-bg-secondaryLight sp-h-[40px] sp-mt-[4px] sp-rounded-xs sp-w-full"></div>
    </div>
  </div>
`;

const UPSELL_SWIPER_STYLES = JSON.stringify([
  `:host .swiper { padding-bottom: 24px; }`,
  `:host .swiper-wrapper { align-items: stretch; }`,
  `:host .swiper-slide { height: auto; }`,
  `:host .swiper-slide > * { height: 100%; }`,
  `:host .swiper-pagination-progressbar { top: auto !important; bottom: 0; height: 4px; width: calc(100% - 48px); left:24px; background: var(--color-color-styleguide-light-gray, #EEEDED); }`,
  `:host .swiper-pagination-progressbar .swiper-pagination-progressbar-fill { background: var(--color-color-styleguide-black, #231F20); }`,
]);

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.locale = this.dataset.locale || false;

    // Register interaction handlers unconditionally — both render paths need them.
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('submit', this.handleSubmit.bind(this));

    const source = this.querySelector('[data-upsell-source]');
    if (!source) return;

    const existing = source.querySelectorAll('article[data-href*="/products/"]');
    if (existing.length) {
      this.extractAndRender(source);
      return;
    }

    let renderTimer;
    let fallbackTimer;
    let drawerObserver;

    const observer = new MutationObserver(() => {
      const cards = source.querySelectorAll('article[data-href*="/products/"]');
      if (!cards.length) return;
      clearTimeout(renderTimer);
      renderTimer = setTimeout(() => {
        observer.disconnect();
        drawerObserver?.disconnect();
        clearTimeout(fallbackTimer);
        this.extractAndRender(source);
      }, 80);
    });
    observer.observe(source, { childList: true, subtree: true });

    const startFallback = () => {
      clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(() => {
        observer.disconnect();
        drawerObserver?.disconnect();
        if (!this.querySelector('intersection-loader, [data-upsell-card]')) this.style.display = 'none';
      }, 5000);
    };

    // Re-insert the SearchSpring script so it re-processes in a visible context.
    // The drawer hides via visibility:hidden when closed, which can prevent rendering.
    const tryInjectScript = () => {
      if (source.querySelector('article[data-href*="/products/"]')) return;
      const script = source.querySelector('script[type="searchspring/personalized-recommendations"]');
      if (script) {
        source.removeChild(script);
        source.appendChild(script);
      }
    };

    const cartDrawer = this.closest('cart-drawer');
    if (cartDrawer) {
      if (cartDrawer.classList.contains('active')) {
        tryInjectScript();
      } else {
        drawerObserver = new MutationObserver(() => {
          if (!cartDrawer.classList.contains('active')) return;
          drawerObserver.disconnect();
          tryInjectScript();
          startFallback();
        });
        drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    startFallback();
  }

  extractAndRender(source) {
    const handles = [...source.querySelectorAll('article[data-href*="/products/"]')]
      .map(a => a.getAttribute('data-href').split('/products/')[1]?.split('?')[0])
      .filter(Boolean);

    if (!handles.length) { this.style.display = 'none'; return; }
    this.renderTiles(handles);
  }

  renderTiles(handles) {
    const grid = this.querySelector('[data-upsell-grid]');
    if (!grid) return;

    const prefix = this.locale ? `/${this.locale}` : '';

    grid.innerHTML = `
      <swiper-container
        init="false"
        slides-per-view="auto"
        space-between="2"
        slides-offset-before="24"
        slides-offset-after="24"
        grab-cursor="true"
        pagination='{"type":"progressbar"}'
        inject-styles='${UPSELL_SWIPER_STYLES}'
      >
        ${handles.map(handle => `
          <swiper-slide class="!sp-h-auto !sp-w-[186px]">
            <intersection-loader
              data-load-immediately="true"
              data-section-id="upsell-product-card"
              data-url="${prefix}/products/${handle}"
            >${UPSELL_SKELETON}</intersection-loader>
          </swiper-slide>
        `).join('')}
      </swiper-container>
    `;

    customElements.whenDefined('swiper-container').then(() => {
      const swiperEl = grid.querySelector('swiper-container');
      if (!swiperEl || swiperEl.swiper) return;
      swiperEl.initialize();
    });
  }

  handleClick(e) {
    const addBtn = e.target.closest('[data-upsell-add]');
    if (!addBtn) return;
    const productId = addBtn.dataset.upsellAdd;
    const modal = productId
      ? this.querySelector(`[data-upsell-modal="${productId}"]`)
      : addBtn.closest('[data-upsell-card]')?.parentElement?.querySelector('[data-upsell-modal]');
    if (modal) this.openModal(modal);
  }

  // Handles form submissions for single-variant cards (form stays inside cart-upsell).
  handleSubmit(e) {
    if (!e.target.closest('[data-upsell-form]')) return;
    e.preventDefault();
    this.submitForm(e.target, e.submitter, null);
  }

  openModal(modal) {
    this._previousFocus = document.activeElement;

    modal._anchor = { parent: modal.parentNode, next: modal.nextSibling };
    document.body.appendChild(modal);
    modal.classList.remove('sp-hidden');

    const closeHandler = () => this.closeModal(modal);
    modal.querySelector('[data-upsell-close]')?.addEventListener('click', closeHandler, { once: true });
    modal.querySelector('[data-upsell-backdrop]')?.addEventListener('click', closeHandler, { once: true });
    modal.addEventListener('click', (e) => { if (e.target === modal) this.closeModal(modal); }, { once: true });

    const escHandler = (e) => { if (e.key === 'Escape') this.closeModal(modal); };
    document.addEventListener('keydown', escHandler);
    modal._escHandler = escHandler;

    const focusables = [...modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    const trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    modal.addEventListener('keydown', trapHandler);
    modal._trapHandler = trapHandler;

    first?.focus();

    // Modal moves to document.body so its submit won't bubble to cart-upsell.
    // Attach directly to the form.
    const form = modal.querySelector('[data-upsell-form]');
    if (form) {
      const submitHandler = (e) => {
        e.preventDefault();
        this.submitForm(form, e.submitter, modal);
      };
      form.addEventListener('submit', submitHandler);
      modal._submitHandler = { form, fn: submitHandler };
    }
  }

  closeModal(modal) {
    modal.classList.add('sp-hidden');
    modal.style.display = '';

    if (modal._escHandler) {
      document.removeEventListener('keydown', modal._escHandler);
      modal._escHandler = null;
    }
    if (modal._trapHandler) {
      modal.removeEventListener('keydown', modal._trapHandler);
      modal._trapHandler = null;
    }
    if (modal._submitHandler) {
      modal._submitHandler.form.removeEventListener('submit', modal._submitHandler.fn);
      modal._submitHandler = null;
    }

    const { parent, next } = modal._anchor || {};
    if (parent) {
      parent.insertBefore(modal, next || null);
      modal._anchor = null;
    } else {
      modal.remove();
    }

    this._previousFocus?.focus();
    this._previousFocus = null;
  }

  async submitForm(form, submitter, modal) {
    if (submitter) submitter.disabled = true;

    const cart = document.querySelector('cart-drawer');
    if (!cart) {
      if (submitter) submitter.disabled = false;
      return;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const body = new FormData(form);
    if (submitter?.name) body.set(submitter.name, submitter.value);
    body.append('sections', cart.getSectionsToRender().map(s => s.id));
    body.append('sections_url', window.location.pathname);
    cart.setActiveElement(document.activeElement);
    config.body = body;

    const drawerInner = cart.querySelector('.drawer__inner');
    drawerInner?.classList.add('loading');

    try {
      const res = await fetch(routes.cart_add_url, config);
      const data = await res.json();
      if (data.status) throw new Error(data.description || 'add to cart failed');

      if (modal) this.closeModal(modal);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell' });
      cart.renderContents(data);
      cart.classList.remove('is-empty');
    } catch (err) {
      console.error('[cart-upsell] add to cart error', err);
      if (submitter) submitter.disabled = false;
    } finally {
      drawerInner?.classList.remove('loading');
    }
  }
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);

class CartEmptyCallouts extends HTMLElement {
  connectedCallback() {
    const template = document.querySelector('[data-cart-empty-callouts]');
    if (!template || !template.content.childElementCount) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'sp-bg-brandPrimary sp-grid sp-grid-cols-3 sp-gap-[8px] sp-px-[20px] sp-py-[16px] sp-w-full';
    wrapper.appendChild(template.content.cloneNode(true));
    this.appendChild(wrapper);
  }
}

if (!customElements.get('cart-empty-callouts')) customElements.define('cart-empty-callouts', CartEmptyCallouts);
